import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Typography, Dropdown, Input, Table, InputNumber, Button, message } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { RootState, AppDispatch } from '../../../store';
import {
  getCycles,
  getProcesses,
  parseBackendCycles,
  parseBackendProcesses,
  Cycle,
  Process,
} from '../../../services/hierarchyService';
import {
  getControlActivities,
  ControlActivity,
  controlActivityToUpdateRequest,
  updateControl,
} from '../../../services/controlActivitiesService';
import { clearAllProcesses } from '../../../store/slices/hierarchySlice';
import ChevronDownIcon from '../../../assets/icons/ic-chevron-down.svg?react';
import ChevronRightIcon from '../../../assets/icons/ic-chevron-right.svg?react';

const { Title } = Typography;

// ==================== STYLED COMPONENTS (match RiskAssessmentDetailPage) ====================

const PageContainer = styled.div`
  padding: 0;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const PageTitle = styled(Title)`
  margin: 0 !important;
  font-family: Inter !important;
  font-weight: 500 !important;
  font-size: 18px !important;
  line-height: 100% !important;
  letter-spacing: 0.005em !important;
  color: #1f2937 !important;
`;

const SelectorsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
`;

const SelectorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: #f3f4f6;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  color: #6b7280;

  &:hover {
    background: #e5e7eb;
    color: #1f2937;
  }

  &:active {
    background: #d1d5db;
  }

  .anticon {
    font-size: 16px;
  }
`;

const CycleSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    opacity: 0.7;
  }
`;

const CycleText = styled.span`
  font-family: Inter;
  font-weight: 600;
  font-size: 20px;
  line-height: 100%;
  letter-spacing: 0.005em;
  color: #1f2937;
`;

const ChevronIcon = styled.div<{ $color?: string }>`
  display: flex;
  align-items: center;
  color: ${props => props.$color || '#6B7280'};

  svg {
    width: 20px;
    height: 20px;
  }

  svg path {
    stroke: ${props => props.$color || '#6B7280'};
  }
`;

const ProcessSelectorsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const ProcessChipWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProcessChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  height: 28px;
  background: #eff6ff;
  color: #1e40af;
  border-radius: 12px;
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.005em;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    opacity: 0.8;
  }
`;

const ProcessOwnerChip = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 0 12px;
  height: 38px;
  background: #eff6ff;
  color: #1e40af;
  border-radius: 18px;
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.005em;
  margin-left: auto;
`;

const DropdownMenu = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  padding: 8px;
  width: 346px;
`;

const SearchInput = styled(Input)`
  margin-bottom: 8px;
  border-radius: 6px;
  height: 38px;

  .ant-input {
    font-family: Inter;
    font-size: 15px;
  }
`;

const DropdownSection = styled.div`
  margin-bottom: 8px;
`;

const DropdownSectionLabel = styled.div`
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  color: #6b7280;
  padding: 8px 12px 8px 12px;
`;

const CycleDropdownItem = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
  background: ${props => (props.$selected ? '#EFF6FF' : 'white')};

  &:hover {
    background: #f3f4f6;
  }
`;

const CycleDropdownItemText = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #1f2937;
  flex: 1;
`;

const CycleDropdownItemIcon = styled.div`
  display: flex;
  align-items: center;
  color: #6b7280;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ProcessDropdownItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
  margin-bottom: 8px;
  padding: 4px 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ProcessDropdownItemChip = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  height: 28px;
  border-radius: 50px;
  background: ${props => (props.$selected ? '#2563EB' : '#EFF6FF')};
  transition: all 0.3s;

  &:hover {
    opacity: 0.9;
  }
`;

const ProcessDropdownItemText = styled.span<{ $selected?: boolean }>`
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.005em;
  color: ${props => (props.$selected ? '#FFFFFF' : '#1E40AF')};
  flex: 1;
`;

const TableFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 12px;
`;

const ContentWrapper = styled.div`
  position: relative;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 6px;
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #E5E7EB;
    border-top-color: #2563EB;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .text {
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    color: #6B7280;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const TotalRow = styled.div`
  display: flex;
  align-items: stretch;
  background: white;
  border-top: 2px solid #E5E7EB;
`;

const TotalSpacer = styled.div`
  flex: 1;
  padding: 16px;
  border-right: 1px solid #E5E7EB;
`;

const TotalCell = styled.div<{ $ok?: boolean }>`
  width: 240px;
  padding: 16px 12px;
  border-right: 1px solid #E5E7EB;
  font-family: Inter;
  font-weight: 600;
  font-size: 15px;
  line-height: 24px;
  color: ${props => (props.$ok === false ? '#dc2626' : '#1f2937')};

  &:last-child {
    border-right: none;
  }
`;

const TableWrapper = styled.div`
  background: white;
  border-radius: 6px;
  border: 1px solid #E5E7EB;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  width: 100%;
  overflow: hidden;

  .ant-table-wrapper {
    overflow-x: auto;
    max-width: 100%;
  }

  .ant-table {
    font-size: 14px;
    border: none;
  }

  .ant-table-thead > tr > th {
    background: #fff;
    font-family: Inter;
    font-weight: 500;
    color: #6B7280;
    font-size: 14px;
    padding: 16px 12px;
    border-bottom: 1px solid #E5E7EB;
    border-right: 1px solid #E5E7EB;
    white-space: nowrap;

    &:last-child {
      border-right: none;
    }

    &::before {
      display: none !important;
    }
  }

  .ant-table-tbody > tr > td {
    padding: 8px 12px;
    font-size: 14px;
    color: #1F2937;
    border-bottom: 1px solid #E5E7EB;
    border-right: 1px solid #E5E7EB;
    vertical-align: middle;
    background: white;

    &:last-child {
      border-right: none;
    }
  }

  .ant-table-tbody > tr:hover > td {
    background: #F9FAFB;
  }

  .ant-table-tbody > tr.ant-table-row-selected > td,
  .ant-table-tbody > tr.ant-table-row:active > td {
    background: transparent !important;
  }

  .ant-input-number {
    .ant-input-number-handler-wrap {
      display: none !important;
    }
  }
`;

const EmptyStateWrapper = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 6px;
`;

const EmptyStateText = styled.div`
  color: #1F2937;
  font-family: Inter;
  font-size: 15px;
  font-weight: 500;
`;

interface LocationState {
  cycleId?: string;
  processId?: string;
}


const BulkWeightingManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const navState = (location.state as LocationState | null) ?? null;

  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  const annualReportId = selectedReport?.annualReportId;

  const [cycles, setCycles] = useState<Cycle[]>([]);
  const [loadingCycles, setLoadingCycles] = useState(true);
  const [selectedCycleId, setSelectedCycleId] = useState<string | null>(null);

  const [processes, setProcesses] = useState<Process[]>([]);
  const [loadingProcesses, setLoadingProcesses] = useState(false);

  const [selectedProcessId, setSelectedProcessId] = useState<string | null>(null);

  const [controls, setControls] = useState<ControlActivity[]>([]);
  const [loadingControls, setLoadingControls] = useState(false);

  /** Draft values keyed by control id */
  const [draftById, setDraftById] = useState<
    Record<string, { weight: number | null; budgetHours: number | null }>
  >({});

  const [initialSnapshot, setInitialSnapshot] = useState<
    Record<string, { weight: number | null; budgetHours: number | null }>
  >({});

  const [cycleSearchTerm, setCycleSearchTerm] = useState('');
  const [processSearchTerm, setProcessSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);

  const [cycleDropdownOpen, setCycleDropdownOpen] = useState(false);
  const [processDropdownOpen, setProcessDropdownOpen] = useState(false);

  const selectedCycle = useMemo(
    () => cycles.find(c => c.id === selectedCycleId) ?? null,
    [cycles, selectedCycleId],
  );

  const selectedProcess = useMemo(
    () => processes.find(p => p.id === selectedProcessId) ?? null,
    [processes, selectedProcessId],
  );

  const filteredCycles = useMemo(() => {
    if (!cycleSearchTerm) {
      return cycles;
    }
    const t = cycleSearchTerm.toLowerCase();
    return cycles.filter(
      c => c.cycleCode.toLowerCase().includes(t) || c.cycleName.toLowerCase().includes(t),
    );
  }, [cycleSearchTerm, cycles]);

  const filteredProcesses = useMemo(() => {
    if (!processSearchTerm) {
return processes;
}
    const t = processSearchTerm.toLowerCase();
    return processes.filter(
      p => p.processCode.toLowerCase().includes(t) || p.processName.toLowerCase().includes(t),
    );
  }, [processSearchTerm, processes]);

  // Load cycles (no Redux cache — direct fetch)
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!annualReportId) {
        setLoadingCycles(false);
        return;
      }
      setLoadingCycles(true);
      try {
        const res = await getCycles(annualReportId, {
          requiresCounts: true,
          skip: 0,
          take: 1000,
          sorted: [{ name: 'CycleCode', direction: 'ascending' }],
        });
        if (cancelled) {
          return;
        }
        const parsed = parseBackendCycles(res.result);
        setCycles(parsed);

        if (navState?.cycleId && parsed.some(c => c.id === navState.cycleId)) {
          setSelectedCycleId(navState.cycleId);
        } else if (parsed.length === 1) {
          setSelectedCycleId(parsed[0].id);
        } else {
          setTimeout(() => {
            setCycleDropdownOpen(true);
          }, 200);
        }
      } catch {
        message.error('Failed to load cycles');
      } finally {
        if (!cancelled) {
          setLoadingCycles(false);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [annualReportId, navState?.cycleId]);

  // Load processes when cycle changes
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!selectedCycleId) {
        setProcesses([]);
        setSelectedProcessId(null);
        return;
      }
      setLoadingProcesses(true);
      try {
        const res = await getProcesses(selectedCycleId, {
          requiresCounts: true,
          skip: 0,
          take: 1000,
          sorted: [{ name: 'ProcessCode', direction: 'ascending' }],
        });
        if (cancelled) {
          return;
        }
        const parsed = parseBackendProcesses(res.result, selectedCycleId);
        setProcesses(parsed);

        if (navState?.processId && parsed.some(p => p.id === navState.processId)) {
          setSelectedProcessId(navState.processId);
        } else {
          setSelectedProcessId(null);
        }
      } catch {
        message.error('Failed to load processes');
        setProcesses([]);
      } finally {
        if (!cancelled) {
          setLoadingProcesses(false);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [selectedCycleId, navState?.processId]);

  // Load in-scope controls for selected process
  const loadControls = useCallback(async () => {
    if (!annualReportId || !selectedProcessId) {
      setControls([]);
      setDraftById({});
      setInitialSnapshot({});
      return;
    }
    setLoadingControls(true);
    try {
      const { data } = await getControlActivities({
        annualReportId,
        processIds: [selectedProcessId],
        testingScope: true,
        sorted: [{ name: 'ControlCode', direction: 'ascending' }],
      });
      setControls(data);
      const next: Record<string, { weight: number | null; budgetHours: number | null }> = {};
      data.forEach(c => {
        next[c.id] = {
          weight: c.controlWeight ?? null,
          budgetHours: c.testing?.budgetHoursToTest ?? null,
        };
      });
      setDraftById(next);
      setInitialSnapshot(JSON.parse(JSON.stringify(next)));
    } catch {
      message.error('Failed to load controls');
      setControls([]);
    } finally {
      setLoadingControls(false);
    }
  }, [annualReportId, selectedProcessId]);

  useEffect(() => {
    loadControls();
  }, [loadControls]);

  const hasChanges = useMemo(() => {
    return controls.some(c => {
      const d = draftById[c.id];
      const i = initialSnapshot[c.id];
      if (!d || !i) {
        return false;
      }
      return d.weight !== i.weight || d.budgetHours !== i.budgetHours;
    });
  }, [controls, draftById, initialSnapshot]);

  const totalWeight = useMemo(() => {
    return controls.reduce((sum, c) => {
      const w = draftById[c.id]?.weight;
      const num = w === null || w === undefined ? 0 : Number(w);
      return sum + (Number.isFinite(num) ? num : 0);
    }, 0);
  }, [controls, draftById]);

  const totalBudgetHours = useMemo(() => {
    return controls.reduce((sum, c) => {
      const h = draftById[c.id]?.budgetHours;
      const num = h === null || h === undefined ? 0 : Number(h);
      return sum + (Number.isFinite(num) ? num : 0);
    }, 0);
  }, [controls, draftById]);

  const totalOk = Math.abs(totalWeight - 100) < 0.001;

  const handleCycleSelect = (cycleId: string) => {
    setCycleSearchTerm('');
    setSelectedCycleId(cycleId);
    setSelectedProcessId(null);
    setCycleDropdownOpen(false);
    setTimeout(() => {
      setProcessDropdownOpen(true);
    }, 150);
  };

  const handleProcessSelect = (processId: string) => {
    setProcessSearchTerm('');
    setSelectedProcessId(processId);
    setProcessDropdownOpen(false);
  };

  const handleSave = async () => {
    if (!hasChanges || !annualReportId) {
      return;
    }
    setSaving(true);
    try {
      for (const c of controls) {
        const d = draftById[c.id];
        const i = initialSnapshot[c.id];
        if (!d || !i) {
          continue;
        }
        if (d.weight === i.weight && d.budgetHours === i.budgetHours) {
          continue;
        }

        const req = controlActivityToUpdateRequest(c, {
          weight: d.weight ?? 0,
          budgetHours:
            d.budgetHours === null || d.budgetHours === undefined ? undefined : d.budgetHours,
        });
        await updateControl(req);
      }
      message.success('Changes saved');
      dispatch(clearAllProcesses());
      setInitialSnapshot(JSON.parse(JSON.stringify(draftById)));
      await loadControls();
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const cycleDropdownContent = (
    <DropdownMenu onClick={e => e.stopPropagation()}>
      <SearchInput
        placeholder="Search for cycle"
        value={cycleSearchTerm}
        onChange={e => setCycleSearchTerm(e.target.value)}
        autoFocus
      />
      <DropdownSection>
        {cycleSearchTerm === '' && <DropdownSectionLabel>All cycles</DropdownSectionLabel>}
        {filteredCycles.map(cycle => {
          const isSelected = selectedCycle?.id === cycle.id;
          return (
            <CycleDropdownItem key={cycle.id} $selected={isSelected} onClick={() => handleCycleSelect(cycle.id)}>
              <CycleDropdownItemText>
                {cycle.cycleCode} {cycle.cycleName}
              </CycleDropdownItemText>
              <CycleDropdownItemIcon>
                <ChevronRightIcon />
              </CycleDropdownItemIcon>
            </CycleDropdownItem>
          );
        })}
      </DropdownSection>
    </DropdownMenu>
  );

  const processDropdownContent = (
    <DropdownMenu onClick={e => e.stopPropagation()}>
      <SearchInput
        placeholder="Search for process"
        value={processSearchTerm}
        onChange={e => setProcessSearchTerm(e.target.value)}
        autoFocus
      />
      <DropdownSection>
        {processSearchTerm === '' && <DropdownSectionLabel>All processes</DropdownSectionLabel>}
        {filteredProcesses.map(p => {
          const isSelected = selectedProcess?.id === p.id;
          return (
            <ProcessDropdownItem key={p.id} onClick={() => !isSelected && handleProcessSelect(p.id)}>
              <ProcessDropdownItemChip $selected={isSelected}>
                <ProcessDropdownItemText $selected={isSelected}>
                  {p.processCode} {p.processName}
                </ProcessDropdownItemText>
              </ProcessDropdownItemChip>
            </ProcessDropdownItem>
          );
        })}
      </DropdownSection>
    </DropdownMenu>
  );

  const columns = [
    {
      title: 'Control ID',
      dataIndex: 'controlCode',
      key: 'controlCode',
      width: 120,
    },
    {
      title: 'Description (What)',
      key: 'what',
      render: (_: unknown, row: ControlActivity) => row.controlDescription?.what || '',
    },
    {
      title: 'Weight (%)',
      key: 'weight',
      width: 240,
      render: (_: unknown, row: ControlActivity) => (
        <InputNumber
          min={0}
          max={100}
          precision={0}
          controls={false}
          style={{ width: '100%' }}
          value={draftById[row.id]?.weight ?? undefined}
          onChange={val =>
            setDraftById(prev => ({
              ...prev,
              [row.id]: {
                ...prev[row.id],
                weight: val === null ? null : Number(val),
                budgetHours: prev[row.id]?.budgetHours ?? null,
              },
            }))
          }
        />
      ),
    },
    {
      title: 'Budget Hours',
      key: 'budgetHours',
      width: 240,
      render: (_: unknown, row: ControlActivity) => (
        <InputNumber
          min={0}
          precision={0}
          controls={false}
          style={{ width: '100%' }}
          value={draftById[row.id]?.budgetHours ?? undefined}
          onChange={val =>
            setDraftById(prev => ({
              ...prev,
              [row.id]: {
                ...prev[row.id],
                weight: prev[row.id]?.weight ?? null,
                budgetHours: val === null ? null : Number(val),
              },
            }))
          }
        />
      ),
    },
  ];

  if (!annualReportId) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle level={2}>Control Weighting</PageTitle>
        </PageHeader>
        <p style={{ color: '#6b7280' }}>Please select an annual report.</p>
      </PageContainer>
    );
  }

  if (loadingCycles) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle level={2}>Control Weighting</PageTitle>
        </PageHeader>
        <p style={{ color: '#6b7280' }}>Loading…</p>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle level={2}>Control Weighting</PageTitle>
      </PageHeader>

        <SelectorsSection>
          <SelectorRow>
            <BackButton onClick={() => navigate('/control-activities')} title="Back to Control Activities">
              <LeftOutlined />
            </BackButton>
            <Dropdown
              dropdownRender={() => cycleDropdownContent}
              trigger={['click']}
              placement="bottomLeft"
              open={cycleDropdownOpen}
              onOpenChange={setCycleDropdownOpen}
            >
              <CycleSelector>
                <CycleText>
                  Cycle:{' '}
                  {selectedCycle
                    ? `${selectedCycle.cycleCode} - ${selectedCycle.cycleName}`
                    : 'Select cycle'}
                </CycleText>
                <ChevronIcon $color="#1F2937">
                  <ChevronDownIcon />
                </ChevronIcon>
              </CycleSelector>
            </Dropdown>
          </SelectorRow>

          <SelectorRow>
            <Dropdown
              dropdownRender={() => processDropdownContent}
              trigger={['click']}
              placement="bottomLeft"
              disabled={!selectedCycleId || loadingProcesses}
              open={processDropdownOpen}
              onOpenChange={setProcessDropdownOpen}
            >
              <ProcessSelectorsWrapper>
                {selectedProcess && (
                  <ProcessChipWrapper>
                    <ProcessChip onClick={() => {}}>
                      {selectedProcess.processCode} {selectedProcess.processName}
                    </ProcessChip>
                  </ProcessChipWrapper>
                )}
                <ChevronIcon $color="#6B7280">
                  <ChevronDownIcon />
                </ChevronIcon>
              </ProcessSelectorsWrapper>
            </Dropdown>

            {selectedProcess?.processOwner && (
              <ProcessOwnerChip>Process Owner: {selectedProcess.processOwner}</ProcessOwnerChip>
            )}
          </SelectorRow>
        </SelectorsSection>

      {!selectedProcessId ? (
        <EmptyStateWrapper>
          <EmptyStateText>
            {!selectedCycleId
              ? 'Select a cycle and process to view and edit control weights'
              : loadingProcesses
                ? 'Loading processes…'
                : processes.length === 0
                  ? 'No processes in this cycle'
                  : 'Select a process to load in-scope controls'}
          </EmptyStateText>
        </EmptyStateWrapper>
      ) : (
        <ContentWrapper>
          {loadingControls && (
            <LoadingOverlay>
              <LoadingSpinner>
                <div className="spinner" />
                <div className="text">Loading controls...</div>
              </LoadingSpinner>
            </LoadingOverlay>
          )}
          <TableWrapper>
            <Table<ControlActivity>
              rowKey="id"
              dataSource={controls}
              columns={columns}
              pagination={false}
              scroll={{ x: 900 }}
            />
            {controls.length > 0 && (
              <TotalRow>
                <TotalCell style={{ width: '120px' }}>Total</TotalCell>
                <TotalSpacer />
                <TotalCell $ok={totalOk ? undefined : false}>
                  {totalWeight.toFixed(0)}%{!totalOk && ' (should equal 100%)'}
                </TotalCell>
                <TotalCell>
                  {totalBudgetHours.toFixed(0)} hrs
                </TotalCell>
              </TotalRow>
            )}
          </TableWrapper>
          <TableFooter>
            <Button
              type="primary"
              onClick={handleSave}
              disabled={!hasChanges || saving || loadingControls}
              loading={saving}
            >
              Save Changes
            </Button>
          </TableFooter>
        </ContentWrapper>
      )}
    </PageContainer>
  );
};

export default BulkWeightingManagementPage;
